import React from 'react'
import i18n from '../i18n'
import { login } from 'services/authService'
import showErrorMessage from '../services/errorHandlerService'
import { removeToken, saveToken } from 'utils/localStorageUtils'
import { Role } from 'utils/roles'
import { decodeToken } from './helpers'

export interface IAuthContext {
  isAuthenticated: boolean
  role?: Role
  username?: string

  handleLogin(username: string, password: string): Promise<boolean>

  handleLogout(): void
}

const AuthContext = React.createContext<IAuthContext>({
  isAuthenticated: false,
  role: undefined,
  username: undefined,
  handleLogin(username: string, password: string) {
    return Promise.reject('login() not implemented')
  },
  handleLogout() {
    return Error('logout() not implemented')
  },
})

interface IState {
  isAuthenticated: boolean
  token?: string
  role?: Role
  username?: string
}

interface IProps {
  token?: string
}

class AuthProvider extends React.Component<IProps, IState> {
  state = {
    isAuthenticated: !!this.props.token,
    role: decodeToken(this.props.token).role,
    username: decodeToken(this.props.token).username,
  }

  handleLogin = async (username: string, password: string) => {
    try {
      const response = await login(username, password)
      const headers = response.headers

      if (headers && headers.authorization) {
        const token = headers.authorization.substring(7)
        const tokenDecoded = decodeToken(token)

        // Save with bearer too
        saveToken(token)

        this.setState({
          isAuthenticated: true,
          token,
          role: tokenDecoded.role,
          username: tokenDecoded.username,
        })

        return true
      }
      return false
    } catch (e) {
      // overrides the 401 message to unauthorized
      showErrorMessage(e, { 401: i18n.t('api_error_401_login') })
      return false
    }
  }

  handleLogout = () => {
    removeToken()
    this.setState({
      isAuthenticated: false,
      token: undefined,
      role: undefined,
      username: undefined,
    })
  }

  render() {
    return (
      <AuthContext.Provider
        value={{
          isAuthenticated: this.state.isAuthenticated,
          role: this.state.role,
          username: this.state.username,
          handleLogin: this.handleLogin,
          handleLogout: this.handleLogout,
        }}>
        {this.props.children}
      </AuthContext.Provider>
    )
  }
}

const AuthConsumer = AuthContext.Consumer
export { AuthProvider, AuthConsumer, AuthContext }
